import { GetBaseUrl } from "@/utils/ConfigHelper";
import axios from "axios";
import Response from "@/utils/Videomonster/Common/Response";
import { PUBLIC_ERROR_CODE } from "@/utils/Error/Constants";
import { GetPublicErrorCode } from "@/utils/Error/Handler";
import { fileUtils } from "@/utils/AiTool";

/**
 *  @param {String} token
 *  @param {{
 *    base64: string,
 *    ext: string,
 *  }} data
 *  @throws {Error}
 *  @returns {Promise<Response>}
 */

const PostRemoveBackground = async (token, data) => {
  try {
    const url = `${GetBaseUrl()}/apis/ai/remove-background`;
    const response = await axios.post(
      url,
      { base64: data.base64, ext: data.ext },
      {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'arraybuffer',
      }
    );
    const result = response.status === 200;
    const resultData = response.data;

    return new Response(result, `data:image/png;base64,${fileUtils.ArrayBufferToBase64(resultData)}`);
  } catch (e) {
    const publicErrorCode = GetPublicErrorCode(e);
    switch (publicErrorCode) {
      case PUBLIC_ERROR_CODE.ERR_NOT_ENOUGH_POINT:
      case PUBLIC_ERROR_CODE.ERR_INVALID_PARAM:
        return new Response(false, publicErrorCode);
      case PUBLIC_ERROR_CODE.ERR_INTERNAL_ERROR:
      default:
        throw e;
    }
  }
};

/**
 *  @param {String} token
 *  @param {{
 *    base64: string,
 *    ext: string,
 *  }} data
 *  @throws {Error}
 *  @returns {Promise<Response>}
 */

const PostImageUpscale = async (token, data) => {
  try {
    const url = `${GetBaseUrl()}/apis/ai/upscale`;
    const response = await axios.post(
      url,
      { base64: data.base64, ext: data.ext },
      {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "arraybuffer",
      }
    );

    const result = response.status === 200;
    const resultData = response.data;

    return new Response(result, `data:image/png;base64,${fileUtils.ArrayBufferToBase64(resultData)}`);
  } catch (e) {
    const publicErrorCode = GetPublicErrorCode(e);
    switch (publicErrorCode) {
      case PUBLIC_ERROR_CODE.ERR_NOT_ENOUGH_POINT:
      case PUBLIC_ERROR_CODE.ERR_INVALID_PARAM:
        return new Response(false, publicErrorCode);
      case PUBLIC_ERROR_CODE.ERR_INTERNAL_ERROR:
      default:
        throw e;
    }
  }
};

/**
 * @param {String} token
 *  @param {{
 *  base64: string,
 *  ext: string,
 *  extendSize: {
 *    up: number,
 *    down: number,
 *    left: number,
 *    right: number,
 *  }
 * }} data
 * @throws {Error}
 * @returns {Promise<Response>}
 */

const PostExtendImage = async(token, data) => {
  try {
    const url = `${GetBaseUrl()}/apis/ai/extend-image`;
    const response = await axios.post(
      url,
      { base64: data.base64, ext: data.ext, extendSize: data.extendSize },
      {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "arraybuffer",
      }
    );

    const result = response.status === 200;
    const resultData = response.data;

    return new Response(result, `data:image/png;base64,${fileUtils.ArrayBufferToBase64(resultData)}`);
  } catch (e) {
    const publicErrorCode = GetPublicErrorCode(e);
    switch (publicErrorCode) {
      case PUBLIC_ERROR_CODE.ERR_NOT_ENOUGH_POINT:
      case PUBLIC_ERROR_CODE.ERR_INVALID_PARAM:
        return new Response(false, publicErrorCode);
      case PUBLIC_ERROR_CODE.ERR_INTERNAL_ERROR:
      default:
        throw e;
    }
  }
};

/**
 * @param {String} token
 * @param {{
 *  base64: string,
 *  maskBase64: string,
 *  ext: string,
 * }} data
 * @throws {Error}
 * @returns {Promise<Response>}
 *
 **/

const PostEraseObject = async (token, data) => {
  try {
    const url = `${GetBaseUrl()}/apis/ai/erase-object`;
    const response = await axios.post(
      url,
      { base64: data.base64, maskBase64:data.maskBase64 , ext: data.ext },
      {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "arraybuffer",
      }
    );

    const result = response.status === 200;
    const resultData = response.data;

    return new Response(result, `data:image/png;base64,${fileUtils.ArrayBufferToBase64(resultData)}`);
  } catch (e) {
    const publicErrorCode = GetPublicErrorCode(e);
    switch (publicErrorCode) {
      case PUBLIC_ERROR_CODE.ERR_NOT_ENOUGH_POINT:
      case PUBLIC_ERROR_CODE.ERR_INVALID_PARAM:
        return new Response(false, publicErrorCode);
      case PUBLIC_ERROR_CODE.ERR_INTERNAL_ERROR:
      default:
        throw e;
    }
  }
};

/**
 * @param {String} token
 * @param {{
 * base64: string,
 * prompt: string,
 * }} data
 * @throws {Error}
 * @returns {Promise<Response>}
 **/

const PostReplaceBackground = async (token, data) => {
  try {
    const { base64, prompt } = data;
    const url = `${GetBaseUrl()}/apis/ai/replace-background`;
    const response = await axios.post(
      url,
      { base64, prompt },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const result = response.status === 200;
    const resultDatas = response.data.data.map(base64 => `data:image/png;base64,${base64}`);

    return new Response(result, resultDatas);
  } catch (e) {
    const publicErrorCode = GetPublicErrorCode(e);
    switch (publicErrorCode) {
      case PUBLIC_ERROR_CODE.ERR_NOT_ENOUGH_POINT:
      case PUBLIC_ERROR_CODE.ERR_INVALID_PARAM:
        return new Response(false, publicErrorCode);
      case PUBLIC_ERROR_CODE.ERR_INTERNAL_ERROR:
      default:
        throw e;
    }
  }
};

/**
 * @param {String} token
 * @param {{
 * base64: string,
 * prompt: string,
 * ext: string
 * }} data
 * @throws {Error}
 * @returns {Promise<Response>}
 **/
const PostReplaceBackgroundTurbo = async (token, data) => {
  try {
    const { base64, prompt, ext } = data;
    const url = `${GetBaseUrl()}/apis/ai/replace-background-turbo`;
    const response = await axios.post(
      url,
      { base64, prompt, ext },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const result = response.status === 200;
    const resultDatas = response.data.data.map(base64 => `data:image/png;base64,${base64}`);

    return new Response(result, resultDatas);
  } catch (e) {
    const publicErrorCode = GetPublicErrorCode(e);
    switch (publicErrorCode) {
      case PUBLIC_ERROR_CODE.ERR_NOT_ENOUGH_POINT:
      case PUBLIC_ERROR_CODE.ERR_INVALID_PARAM:
        return new Response(false, publicErrorCode);
      case PUBLIC_ERROR_CODE.ERR_INTERNAL_ERROR:
      default:
        throw e;
    }
  }
};

/**
* @param {String} token
* @param {{
*   base64: string,
*   prompt: string,
*   ext: string
* }} data
* @throws {Error}
* @returns {Promise<Response>}
**/
const PostReplaceBackgroundRelight = async (token, data) => {
  try {
    const { base64, prompt, ext } = data;
    const url = `${GetBaseUrl()}/apis/ai/replace-background-relight`;
    const response = await axios.post(
      url,
      { base64, prompt, ext },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const result = response.status === 200;
    const resultDatas = response.data.data;

    return new Response(result, resultDatas);
  } catch (e) {
    const publicErrorCode = GetPublicErrorCode(e);
    switch (publicErrorCode) {
      case PUBLIC_ERROR_CODE.ERR_NOT_ENOUGH_POINT:
      case PUBLIC_ERROR_CODE.ERR_INVALID_PARAM:
        return new Response(false, publicErrorCode);
      case PUBLIC_ERROR_CODE.ERR_INTERNAL_ERROR:
      default:
        throw e;
    }
  }
}

/**
 * @param {String} token
 * @param {{
 * productName: string,
 * productDesc: string,
 * curProductCateogory: string,
 * curProductCateogoryDetail: string,
 * curKeyword: string,
 * curEmotion: string,
 * curPurpose: string,
 * curLength: string,
 * curAge: string,
 * curGender: string,
 * curSeason: string,
 * curSocialMedia: string,
 * curMediaType: string,
 * curLanguage: string,
 * lineBreak: string
 * }} data
 * @throws {Error}
 * @returns {Promise<Response>}
 */

const PostGenerateCopyright = async (token, data) => {
  try {
    const url = `${GetBaseUrl()}/apis/ai/generate-copyright`;
    const response = await axios.post(
      url,
      data,
      { headers: { Authorization: `Bearer ${token}` } } 
    );

    const result = response.status === 200;
    const resultData = response.data.data.result;

    return new Response(result, resultData);
  } catch (e) {
    const publicErrorCode = GetPublicErrorCode(e);
    switch (publicErrorCode) {
      case PUBLIC_ERROR_CODE.ERR_NOT_ENOUGH_POINT:
      case PUBLIC_ERROR_CODE.ERR_INVALID_PARAM:
        return new Response(false, publicErrorCode);
      case PUBLIC_ERROR_CODE.ERR_INTERNAL_ERROR:
      default:
        throw e;
    }
  }
};

/**
 * @param {String} token
 * @param {{
 * content: string,
 * targetLanguage?: string
 * }} data
 * @throws {Error}
 * @returns {Promise<Response>}
 */
const PostTranslate = async (token, data) => {
  try {
    const { content } = data;
    const targetLanguage = data.targetLanguage || "en";

    const url = `${GetBaseUrl()}/apis/ai/translate`;
    const body = { 
      content,
      targetLanguage
    }
    const config = { headers: { Authorization: `Bearer ${token}` } };
    const response = await axios.post(
      url,
      body,
      config
    );

    const result = response.status === 200;
    const resultData = response.data.data.result;

    return new Response(result, resultData);
  } catch (e) {
    const publicErrorCode = GetPublicErrorCode(e);
    switch (publicErrorCode) {
      case PUBLIC_ERROR_CODE.ERR_NOT_ENOUGH_POINT:
      case PUBLIC_ERROR_CODE.ERR_INVALID_PARAM:
        return new Response(false, publicErrorCode);
      case PUBLIC_ERROR_CODE.ERR_INTERNAL_ERROR:
      default:
        throw e;
    }
  }
}

const PostInpaint = async (token, data) => {
  try {
    const { base64, ext, prompt, maskBase64 } = data;
    const url = `${GetBaseUrl()}/apis/ai/inpaint`;
    const body = {
      base64,
      ext,
      prompt,
      maskBase64
    }
    const config = { 
      headers: { Authorization: `Bearer ${token}` },
      responseType: "arraybuffer",
    };
    const response = await axios.post(url, body, config);

    const result = response.status === 200;
    const resultData = response.data;

    return new Response(result, `data:image/png;base64,${fileUtils.ArrayBufferToBase64(resultData)}`);
  }
  catch (e) {
    const publicErrorCode = GetPublicErrorCode(e);
    switch (publicErrorCode) {
      case PUBLIC_ERROR_CODE.ERR_NOT_ENOUGH_POINT:
      case PUBLIC_ERROR_CODE.ERR_INVALID_PARAM:
        return new Response(false, publicErrorCode);
      case PUBLIC_ERROR_CODE.ERR_INTERNAL_ERROR:
      default:
        throw e;
    }
  }
}

/**
 * @param {String} token
 * @param {{
 *  inputMySourceId: string,
 *  controlStrength: number,
 *  prompt: string
 * }} data
 * @throws {Error}
 * @returns {Promise<Response>}
 */
const PostSketchToImage = async (token, data) => {
  try {
    const { inputMySourceId, ext, controlStrength, prompt } = data;
    const url = `${GetBaseUrl()}/apis/ai/sketch-to-image`;
    const body = {
      inputMySourceId,
      controlStrength,
      prompt
    }
    const config = { 
      headers: { Authorization: `Bearer ${token}` }
    };
    const response = await axios.post(url, body, config);

    const result = response.status === 200;
    const resultData = response.data.data;

    return new Response(result, resultData);
  } catch (e) {
    const publicErrorCode = GetPublicErrorCode(e);
    switch (publicErrorCode) {
      case PUBLIC_ERROR_CODE.ERR_NOT_ENOUGH_POINT:
      case PUBLIC_ERROR_CODE.ERR_INVALID_PARAM:
        return new Response(false, publicErrorCode);
      case PUBLIC_ERROR_CODE.ERR_INTERNAL_ERROR:
      default:
        throw e;
    }
  }
}

/**
 * @param {String} token
 * @param {{
 *  inputMySourceId: string,
 *  prompt: string,
 *  duration: number,
 *  cameraMovement?: string
 * }} data
 * @throws {Error}
 * @returns {Promise<Response>}
 */
const PostImageToVideo = async (token, data) => {
  try {
    const { inputMySourceId, prompt, duration, cameraMovement } = data;
    const url = `${GetBaseUrl()}/apis/ai/image-to-video`;
    const body = {
      inputMySourceId,
      prompt,
      duration,
    }
    if (cameraMovement) body.cameraMovement = cameraMovement;
    const config = {
      headers: { Authorization: `Bearer ${token}` },
    };
    const response = await axios.post(url, body, config);

    const result = response.status === 200;
    const resultData = response.data.data;

    return new Response(result, resultData);
  } catch (e) {
    const publicErrorCode = GetPublicErrorCode(e);
    switch (publicErrorCode) {
      case PUBLIC_ERROR_CODE.ERR_NOT_ENOUGH_POINT:
      case PUBLIC_ERROR_CODE.ERR_INVALID_PARAM:
        return new Response(false, publicErrorCode);
      case PUBLIC_ERROR_CODE.ERR_INTERNAL_ERROR:
      default:
        throw e;
    }
  }
}


export default {
  PostRemoveBackground,
  PostImageUpscale,
  PostExtendImage,
  PostEraseObject,
  PostReplaceBackground,
  PostReplaceBackgroundTurbo,
  PostReplaceBackgroundRelight,
  PostGenerateCopyright,
  PostTranslate,
  PostInpaint,
  PostSketchToImage,
  PostImageToVideo,
};
